import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Printer } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import CrudSection from "@/components/CrudSection";
import { PageHeader, StatusBadge, fmtDate } from "@/components/common";
import AttachmentsCell from "@/components/AttachmentsCell";
import { listResource } from "@/lib/api";

const STATUSES = ["not_started", "working", "waiting_parts", "ready_for_pdi", "ready_for_acr", "completed"];

const fields = [
  { key: "shop_type", label: "Type", type: "select", default: "internal", required: true, options: ["internal", "customer"] },
  { key: "stock_number", label: "Stock #", type: "text", required: true, placeholder: "N-4412" },
  { key: "unit", label: "Unit", type: "text", required: true, placeholder: "JD 6120M Tractor" },
  { key: "condition", label: "Condition", type: "select", default: "new", required: true, options: ["new", "trade", "internal", "warranty"] },
  { key: "status", label: "Status", type: "select", default: "not_started", required: true, options: STATUSES },
  { key: "job", label: "Job", type: "text", full: true, required: true, placeholder: "PDI, assembly, recondition…" },
  { key: "initials", label: "Your Initials", type: "text", required: true, maxLength: 2, placeholder: "e.g. BW" },
  { key: "notes", label: "Notes", type: "textarea", full: true },
];

const colStock = { key: "stock_number", label: "Stock #", mono: true };
const colUnit = { key: "unit", label: "Unit" };
const colCondition = { key: "condition", label: "Condition", render: (r) => <span className="text-sm capitalize">{r.condition || "—"}</span> };
const colJob = { key: "job", label: "Job" };
const colInitials = { key: "initials", label: "Entered By", render: (r) => <span className="font-mono-data uppercase">{r.initials || "—"}</span> };
const colNotes = { key: "notes", label: "Notes", render: (r) => <span className="text-sm text-gray-600">{r.notes || "—"}</span> };
const colAttach = { key: "attachments", label: "Photos", render: (r) => <AttachmentsCell row={r} path="shopjobs" label="Photos" /> };
const colStatusEditable = { key: "status", label: "Status", editable: true, options: STATUSES, render: (r) => <StatusBadge status={r.status} /> };

const activeColumns = [colStock, colUnit, colCondition, colStatusEditable, colJob, colInitials, colNotes, colAttach];

const archivedColumns = [
  colStock, colUnit,
  { key: "shop_type", label: "Type", render: (r) => <span className="text-sm capitalize">{r.shop_type || "internal"}</span> },
  colCondition,
  { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  colJob, colInitials,
  { key: "archived_at", label: "Completed", mono: true, render: (r) => r.archived_at ? fmtDate(r.archived_at.slice(0, 10)) : <span className="text-gray-300">—</span> },
  colNotes, colAttach,
];

const isCustomer = (r) => (r.shop_type || "internal") === "customer";

const tabBtn = "rounded-none border-2 border-gray-900 px-4 py-2 font-bold uppercase tracking-wide text-xs data-[state=active]:bg-gray-900 data-[state=active]:text-white";

export default function NorthShop() {
  const { data: jobs = [] } = useQuery({ queryKey: ["shopjobs"], queryFn: () => listResource("shopjobs"), refetchInterval: 5000 });
  const [archQ, setArchQ] = useState("");
  const internalCount = jobs.filter((j) => !j.archived && !isCustomer(j)).length;
  const customerCount = jobs.filter((j) => !j.archived && isCustomer(j)).length;
  const archivedCount = jobs.filter((j) => j.archived).length;

  const archMatch = (r) => {
    if (!r.archived) return false;
    const q = archQ.trim().toLowerCase();
    if (!q) return true;
    return [r.stock_number, r.unit, r.job, r.initials, r.notes, r.shop_type]
      .some((v) => (v || "").toString().toLowerCase().includes(q));
  };

  const fmtD = (s) => (s ? new Date(s.slice(0, 10) + "T00:00:00").toLocaleDateString([], { month: "numeric", day: "numeric", year: "2-digit" }) : "—");
  const printArchive = () =>
    printRecords({
      title: `North Shop — Archived${archQ.trim() ? ` (filter: "${archQ.trim()}")` : ""}`,
      subtitle: "Completed jobs",
      path: "shopjobs",
      records: jobs.filter(archMatch),
      columns: [
        { label: "Stock #", value: (r) => r.stock_number },
        { label: "Unit", value: (r) => r.unit },
        { label: "Type", value: (r) => r.shop_type || "internal" },
        { label: "Condition", value: (r) => r.condition },
        { label: "Status", value: (r) => (r.status || "").replace(/_/g, " ") },
        { label: "Job", value: (r) => r.job },
        { label: "By", value: (r) => r.initials },
        { label: "Completed", value: (r) => fmtD(r.archived_at) },
        { label: "Notes", value: (r) => r.notes },
      ],
    });

  return (
    <div data-testid="north-shop-section">
      <PageHeader title="North Shop" subtitle="Set-up shop — new & used/trade-in unit assembly, PDI and recondition" />
      <Tabs defaultValue="internal" className="w-full">
        <TabsList className="mb-6 flex flex-wrap h-auto justify-start gap-2 bg-transparent p-0">
          <TabsTrigger value="internal" data-testid="north-shop-tab-internal" className={tabBtn}>
            Internal{internalCount ? ` (${internalCount})` : ""}
          </TabsTrigger>
          <TabsTrigger value="customer" data-testid="north-shop-tab-customer" className={tabBtn}>
            Customer{customerCount ? ` (${customerCount})` : ""}
          </TabsTrigger>
          <TabsTrigger value="archived" data-testid="north-shop-tab-archived" className={tabBtn}>
            Archived{archivedCount ? ` (${archivedCount})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="internal" className="mt-0">
          <CrudSection
            title="Internal Jobs"
            subtitle="Company units & shop work"
            path="shopjobs"
            testPrefix="north-shop-internal"
            addLabel="New Job"
            editLabel="Edit Job"
            fields={[{ ...fields[0], default: "internal" }, ...fields.slice(1)]}
            columns={activeColumns}
            filter={(r) => !r.archived && !isCustomer(r)}
            noDelete
            searchable
          />
        </TabsContent>

        <TabsContent value="customer" className="mt-0">
          <CrudSection
            title="Customer Jobs"
            subtitle="Sold units & customer machines (auto-added from Recently Sold)"
            path="shopjobs"
            testPrefix="north-shop-customer"
            addLabel="New Job"
            editLabel="Edit Job"
            fields={[{ ...fields[0], default: "customer" }, ...fields.slice(1)]}
            columns={activeColumns}
            filter={(r) => !r.archived && isCustomer(r)}
            noDelete
            searchable
          />
        </TabsContent>

        <TabsContent value="archived" className="mt-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                data-testid="north-shop-archive-search"
                value={archQ}
                onChange={(e) => setArchQ(e.target.value)}
                placeholder="Search archived jobs — stock #, unit, job…"
                className="rounded-none border-2 border-gray-900 pl-9"
              />
            </div>
            <Button
              data-testid="north-shop-archive-print"
              onClick={printArchive}
              variant="outline"
              className="rounded-none border-2 border-gray-900 font-bold uppercase tracking-wide hover:bg-gray-900 hover:text-white"
            >
              <Printer className="w-4 h-4 mr-2" /> Print / Export
            </Button>
          </div>
          <CrudSection
            title="Archived — Completed Work"
            subtitle="Permanent, locked record of completed jobs"
            path="shopjobs"
            testPrefix="north-shop-archived"
            fields={fields}
            columns={archivedColumns}
            filter={archMatch}
            readOnly
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
